import { Paginator } from '@crehana/compass.ui/paginator';
import {
  Table,
  TableBody,
  TableCell,
  TableContent,
  TableEmptyResults,
  TableErrorState,
  TableFooter,
  TableHead,
  TableHeader,
  TableLoading,
  TableMainHeader,
  TableRow,
  TableSearch,
  TableTitleAndCounter,
  TableTop,
} from '@crehana/compass.ui/table';
import {
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type SearchState,
  flexRender,
  useTable,
} from '@crehana/compass.ui/table/tanstack';
import clsx from 'clsx';
import { PREVIEW_TABLE } from '../../constants';
import { useAddResourceBundle } from '../../i18n/use-add-resource-bundle';
import { useTranslationCareerSiteAdminShared } from '../../i18n/use-translations';
import type {
  TListVacanciesProps,
  TTableDepartmentsList,
  TTableVacanciesList,
} from './shared/types';
import { useColumnsListVacancy } from './use-columns-list-vacancy';

type TVacanciesTableProps = {
  data: TTableVacanciesList[];
  loading: boolean;
  error: boolean;
  search: SearchState;
  columnFilters: ColumnFiltersState;
  pagination: PaginationState;
  onSearchChange: OnChangeFn<SearchState>;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  onPaginationChange: OnChangeFn<PaginationState>;
  onRefetch: () => void;
  title?: string;
  color?: TListVacanciesProps['color'];
  previewType?: TListVacanciesProps['previewType'];
  departmentsData?: TTableDepartmentsList[];
  rowCount: number;
  cardBackgroundClassName?: string;
};

export const VacanciesTable = ({
  data,
  loading,
  error,
  search,
  columnFilters,
  pagination,
  onSearchChange,
  onColumnFiltersChange,
  onPaginationChange,
  onRefetch,
  title,
  color,
  previewType,
  departmentsData,
  rowCount,
  cardBackgroundClassName,
}: TVacanciesTableProps) => {
  useAddResourceBundle();
  const { t } = useTranslationCareerSiteAdminShared();
  const columns = useColumnsListVacancy(departmentsData, columnFilters);

  const table = useTable<TTableVacanciesList>({
    data,
    columns: columns.columns,
    rowCount,
    onSearchChange,
    onPaginationChange,
    onColumnFiltersChange,
    state: {
      pagination,
      columnFilters,
      search,
    },
  });

  return (
    <Table
      className={clsx(
        'tw-border-separate tw-border-spacing-0 tw-w-full tw-border-none tw-px-0',
        cardBackgroundClassName,
      )}
    >
      {/* Table header */}
      <TableTop className="tw-px-0">
        <TableMainHeader className="tw-flex sm:tw-flex-wrap tw-items-center tw-justify-between tw-px-0">
          <div
            className={clsx(
              'tw-flex tw-gap-2',
              previewType === PREVIEW_TABLE.MOBILE && 'tw-flex-col',
            )}
          >
            <TableTitleAndCounter
              count={table.getRowCount()}
              title={title || ''}
            />
            <TableSearch
              className="tw-bg-neutral-light-100"
              placeholder={t('SEARCH_BY_NAME')}
              onSearch={table.searchRows}
            />
          </div>
        </TableMainHeader>
      </TableTop>

      {/* Table Content */}
      <TableContent className="tw-px-0 tw-border-separate tw-border-spacing-0 tw-border-spacing-y-1.6 tw-w-full tw-border-none">
        {/* Table header */}
        {previewType !== PREVIEW_TABLE.MOBILE && (
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow
                className="tw-border-none"
                isHeader
                key={headerGroup.id}
              >
                {headerGroup.headers.map(header => (
                  <TableHead
                    key={header.id}
                    className={clsx(
                      'tw-bg-neutral-light-300 last:tw-rounded-r-[72px] first:tw-rounded-l-[72px] ',
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
        )}

        {error && !loading && (
          <TableErrorState
            pageSize={table.pageSize}
            onClickTryAgain={() => {
              onRefetch();
            }}
          />
        )}

        {loading && <TableLoading pageSize={table.pageSize} />}

        {/* Table body */}
        {!loading &&
          !error &&
          previewType !== PREVIEW_TABLE.MOBILE &&
          table.getRowModel().rows?.length > 0 && (
            <TableBody className="tw-border-separate tw-border-spacing-0 tw-w-full ">
              {table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  className="tw-border-separate tw-border-spacing-0 tw-w-full tw-bg-neutral-light-100 tw-border-2 tw-border-blue-500 tw-rounded-[10px] tw-overflow-hidden tw-shadow-01 tw-mb-1.6"
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      className={
                        'tw-w-full tw-px-4 tw-py-2 last:tw-rounded-r-[10px] first:tw-rounded-l-[10px]'
                      }
                      style={{
                        borderLeft:
                          index === 0
                            ? `2px solid ${color?.primaryColor}`
                            : 'none',
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          )}

        {/* Table footer */}
        {!loading && table.getRowCount() > 0 && (
          <TableFooter className="tw-border-none tw-w-full tw-justify-center">
            <Paginator
              variant="numeric"
              totalPages={Math.ceil(rowCount / table.pageSize)}
              onPageChange={page => table.setPageIndex(page - 1)}
              currentPage={table.currentPage + 1}
            />
          </TableFooter>
        )}
      </TableContent>

      {/* Empty State - Outside table structure */}
      {!loading && !error && table.getRowModel().rows?.length === 0 && (
        <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-py-8 tw-bg-neutral-light-100">
          <TableEmptyResults searchKey={search} pageSize={table.pageSize} />
        </div>
      )}
    </Table>
  );
};
